import {useMemo} from 'react';

import {useIsInLoadedReportContext} from '../../../../pages/ReportPage/LoadedReportContext';
import {combinedFilter} from '../../../../state/graphql/runSelectorQuery';
import {type GroupSelections} from '../../../../state/views/groupSelections/types';
import {EMPTY_FILTERS} from '../../../../util/filters';
import {type Filter} from '../../../../util/filterTypes';
import * as QueryTS from '../../../../util/queryts';
import {type Sort} from '../../../../util/queryTypes';
import {type Key} from '../../../../util/runTypes';
import {
  RunColorOptions,
  type SemanticLegendSettingsWithCategoricalConfig,
} from '../types';
import {
  isDefaultSemanticLegendSettings,
  useSemanticLegendSettingsContext,
} from './SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {useMetricStatisticsForRun} from './utils/useMetricStatisticsForRun';
// This hook is used to get the semantic legend settings. There are 3 different sources of truth for the semantic legend settings:
// 1. The semantic legend settings in the semantic legend settings context - this is semantic legend settings that are stored in the table
// 2. The semantic legend settings in the view spec - this is needed for reports
// 3. There is no semantic legend settings and so we should not return anything
// This hook is needed because we need to get the semantic legend settings in the run selector, which doesn't always have the semantic legend settings context
export function useSemanticLegend(
  entityName: string,
  projectName: string,
  filters?: Filter<Key>,
  order?: Sort,
  groupSelections?: GroupSelections,
  search?:
    | {
        query: string;
        isRegex?: boolean;
      }
    | undefined,
  semanticLegendSettingsFromViewSpec?: SemanticLegendSettingsWithCategoricalConfig
) {
  const {
    semanticLegendSettings,
    semanticLegendRunColorOptions,
    specPreviouslyCreated,
  } = useSemanticLegendSettingsContext();

  const runsFilter = combinedFilter(
    filters ?? EMPTY_FILTERS,
    groupSelections ?? undefined,
    undefined,
    undefined,
    search
  );

  const isInReport = useIsInLoadedReportContext();

  // If we're in a report, we need to get the up to date metric statistics for the runs
  const {metricStatisticsForRun} = useMetricStatisticsForRun({
    currentKey: semanticLegendSettingsFromViewSpec?.key ?? '',
    entityName: entityName,
    filters: JSON.stringify(runsFilter),
    order: order ? QueryTS.sortToOrderString(order) : '-createdAt',
    projectName: projectName,
    shouldSkip: isInReport
      ? isDefaultSemanticLegendSettings(
          semanticLegendSettingsFromViewSpec ?? {}
        )
      : true,
  });

  const semanticLegend = useMemo(() => {
    // For reports, we store the semantic legend settings in the view spec
    if (isInReport) {
      if (
        isDefaultSemanticLegendSettings(
          semanticLegendSettingsFromViewSpec ?? {}
        )
      ) {
        return {
          runColorOptions: RunColorOptions.OriginalProjectColors,
          metricStatisticsForRun:
            semanticLegendSettings?.metricStatisticsForRun,
        };
      }
      return {
        ...semanticLegendSettingsFromViewSpec,
        metricStatisticsForRun: metricStatisticsForRun,
      };
    }
    return {
      ...semanticLegendSettings,
      runColorOptions: semanticLegendRunColorOptions,
    };
  }, [
    isInReport,
    semanticLegendSettings,
    semanticLegendRunColorOptions,
    semanticLegendSettingsFromViewSpec,
    metricStatisticsForRun,
  ]);

  const shouldShowSemanticLegend = specPreviouslyCreated || isInReport;
  return useMemo(
    () => (shouldShowSemanticLegend ? semanticLegend : undefined),
    [shouldShowSemanticLegend, semanticLegend]
  );
}
